import React from 'react';
import { CheckCircle, Upload, Download, Ship, Package, FileText, Scale, BookOpen, Gavel } from 'lucide-react';
import type { DeclarationData } from '../types';

interface ReviewViewProps {
  extractedData: DeclarationData | null;
  generateSADForm: () => void;
}

const ReviewView: React.FC<ReviewViewProps> = ({ extractedData, generateSADForm }) => {
  if (!extractedData) {
    return (
      <div className="text-center p-8">
        <h2 className="text-2xl font-bold text-slate-700">No data to review.</h2>
        <p className="text-slate-500">Please upload documents first.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2">Review Extracted Data</h2>
          <p className="text-gray-400">Verify extracted data and tariff classifications (Part III / GIR)</p>
        </div>
        <button
          onClick={generateSADForm}
          className="bg-gradient-to-r from-teal-600 to-blue-700 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-xl transition-all flex items-center gap-2"
        >
          <CheckCircle size={20} />
          Generate SAD Form
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="glass-card rounded-xl p-6">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <div className="p-2 bg-teal-900/50 rounded-lg"><Upload className="text-teal-400" size={20} /></div>
            Exporter Information
          </h3>
          <div className="space-y-3">
            <div><label className="text-sm font-semibold text-gray-400 mb-1 block">Company Name</label><input type="text" defaultValue={extractedData.exporter.name} className="w-full px-4 py-2 border border-gray-600 rounded-lg bg-gray-700 text-gray-200" readOnly /></div>
            <div><label className="text-sm font-semibold text-gray-400 mb-1 block">TIN</label><input type="text" defaultValue={extractedData.exporter.tin} className="w-full px-4 py-2 border border-gray-600 rounded-lg bg-gray-700 text-gray-200 font-mono" readOnly /></div>
            <div><label className="text-sm font-semibold text-gray-400 mb-1 block">Address</label><textarea defaultValue={extractedData.exporter.address} rows={2} className="w-full px-4 py-2 border border-gray-600 rounded-lg bg-gray-700 text-gray-200" readOnly /></div>
          </div>
        </div>
        <div className="glass-card rounded-xl p-6">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <div className="p-2 bg-blue-900/50 rounded-lg"><Download className="text-blue-400" size={20} /></div>
            Importer Information
          </h3>
          <div className="space-y-3">
            <div><label className="text-sm font-semibold text-gray-400 mb-1 block">Company Name</label><input type="text" defaultValue={extractedData.importer.name} className="w-full px-4 py-2 border border-gray-600 rounded-lg bg-gray-700 text-gray-200" readOnly /></div>
            <div><label className="text-sm font-semibold text-gray-400 mb-1 block">TIN</label><input type="text" defaultValue={extractedData.importer.tin} className="w-full px-4 py-2 border border-gray-600 rounded-lg bg-gray-700 text-gray-200 font-mono" readOnly /></div>
            <div><label className="text-sm font-semibold text-gray-400 mb-1 block">Address</label><textarea defaultValue={extractedData.importer.address} rows={2} className="w-full px-4 py-2 border border-gray-600 rounded-lg bg-gray-700 text-gray-200" readOnly /></div>
          </div>
        </div>
      </div>

      <div className="glass-card rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><div className="p-2 bg-purple-900/50 rounded-lg"><Ship className="text-purple-400" size={20} /></div>Shipping Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div><label className="text-sm font-semibold text-gray-400 mb-1 block">Bill of Lading</label><input type="text" defaultValue={extractedData.shipping.billOfLading} className="w-full px-4 py-2 border border-gray-600 font-mono bg-gray-700 text-gray-200 rounded-lg" readOnly /></div>
          <div><label className="text-sm font-semibold text-gray-400 mb-1 block">Vessel Name</label><input type="text" defaultValue={extractedData.shipping.vesselName} className="w-full px-4 py-2 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg" readOnly /></div>
          <div><label className="text-sm font-semibold text-gray-400 mb-1 block">Arrival Date</label><input type="text" defaultValue={extractedData.shipping.arrivalDate} className="w-full px-4 py-2 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg" readOnly /></div>
          <div><label className="text-sm font-semibold text-gray-400 mb-1 block">Port of Discharge</label><input type="text" defaultValue={extractedData.shipping.portOfDischarge} className="w-full px-4 py-2 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg" readOnly /></div>
          <div><label className="text-sm font-semibold text-gray-400 mb-1 block">Incoterms</label><input type="text" defaultValue={extractedData.shipping.incoterms} className="w-full px-4 py-2 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg" readOnly /></div>
        </div>
      </div>

      <div className="glass-card rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><div className="p-2 bg-amber-900/50 rounded-lg"><Package className="text-amber-400" size={20} /></div>Items & Classification ({extractedData.items.length})</h3>
        <div className="space-y-4">
          {extractedData.items.map((item, index) => (
            <div key={index} className="border border-gray-700 rounded-lg p-4 bg-gray-800">
              <div className="flex items-center justify-between mb-3 border-b border-gray-700 pb-2">
                <h4 className="font-bold text-white flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-teal-900 text-teal-400 flex items-center justify-center text-xs">{item.itemNo}</span>
                  Item {item.itemNo}
                </h4>
                <div className="flex gap-2">
                    <span className="px-3 py-1 bg-blue-900/50 text-blue-400 text-xs font-mono rounded-full border border-blue-800">HS: {item.commodityCode}</span>
                    <span className="px-3 py-1 bg-purple-900/50 text-purple-400 text-xs font-mono rounded-full border border-purple-800">
                      {item.legalMethodology?.gir.length ? `GIR ${item.legalMethodology.gir.join(', ')}` : (item.girRule || 'GIR 1')}
                    </span>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                    <label className="text-xs font-semibold text-gray-500 mb-1 block">Description</label>
                    <p className="text-sm text-gray-200">{item.description}</p>
                </div>
                <div><label className="text-xs font-semibold text-gray-500 mb-1 block">Value</label><p className="text-sm font-bold text-gray-200">${item.totalValue.toFixed(2)}</p></div>
                <div><label className="text-xs font-semibold text-gray-500 mb-1 block">Est. Duty</label><p className="text-sm font-bold text-red-400">{item.dutyRate || 'N/A'}</p></div>
                
                <div className="md:col-span-4 bg-gray-900/50 p-4 rounded-lg border border-gray-700/50">
                    <div className="mb-3">
                        <div className="flex items-center gap-2 mb-1">
                          <Scale size={16} className="text-amber-400" />
                          <label className="text-xs font-semibold text-amber-400 uppercase tracking-wider">Classification Rationale</label>
                        </div>
                        <p className="text-sm text-gray-300 leading-relaxed pl-6">{item.classificationRationale || 'Classified based on standard nomenclature description match.'}</p>
                    </div>

                    {(item.primaryLaw || item.legalMethodology) && (
                      <div className="mt-3 pt-3 border-t border-gray-700/50 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {item.primaryLaw && (
                          <div className="flex gap-2">
                            <BookOpen size={14} className="text-teal-400 mt-0.5 flex-shrink-0" />
                            <div>
                              <span className="text-xs font-semibold text-teal-400 block">Primary Law</span>
                              <span className="text-xs text-gray-400">{item.primaryLaw}</span>
                              {item.legalMethodology?.partIIIClause && (
                                <span className="text-xs text-teal-200 block mt-0.5">Section: {item.legalMethodology.partIIIClause}</span>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {(item.legalMethodology?.explanatoryNotes?.length || item.legalMethodology?.rulingsConsidered?.length) && (
                          <div className="flex gap-2">
                             <Gavel size={14} className="text-blue-400 mt-0.5 flex-shrink-0" />
                             <div>
                                <span className="text-xs font-semibold text-blue-400 block">Supporting Authority</span>
                                {item.legalMethodology?.explanatoryNotes?.map((note, i) => (
                                  <span key={i} className="text-xs text-gray-400 block">EN: {note}</span>
                                ))}
                                {item.legalMethodology?.rulingsConsidered?.map((ruling, i) => (
                                  <span key={i} className="text-xs text-gray-400 block">Ruling: {ruling}</span>
                                ))}
                             </div>
                          </div>
                        )}
                      </div>
                    )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="glass-card rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><div className="p-2 bg-emerald-900/50 rounded-lg"><FileText className="text-emerald-400" size={20} /></div>Financial Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4"><p className="text-sm text-gray-400 mb-1">Total Invoice</p><p className="text-2xl font-bold text-white">${extractedData.financial.totalInvoiceAmount.toLocaleString()}</p><p className="text-xs text-gray-500 mt-1">{extractedData.financial.currency}</p></div>
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4"><p className="text-sm text-gray-400 mb-1">Exchange Rate</p><p className="text-2xl font-bold text-white">{extractedData.financial.exchangeRate}</p><p className="text-xs text-gray-500 mt-1">USD to PGK</p></div>
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4"><p className="text-sm text-gray-400 mb-1">Total in PGK</p><p className="text-2xl font-bold text-teal-400">K{extractedData.financial.totalPGK.toLocaleString()}</p><p className="text-xs text-gray-500 mt-1">Papua New Guinea Kina</p></div>
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4"><p className="text-sm text-gray-400 mb-1">Items</p><p className="text-2xl font-bold text-white">{extractedData.items.length}</p><p className="text-xs text-gray-500 mt-1">Commodity items</p></div>
        </div>
      </div>
    </div>
  );
};

export default ReviewView;